import { AddedCommentDocument, AddedCommentSubscription, WatchMode } from '@clipr/lib';
import { action, computed, makeObservable, observable } from 'mobx';
import { WSClient } from '../../api/apiWSClient';
import { isValidUrl } from '../../core/urlUtils';
import { Comment, Reaction } from '../../entities/comment';

import { Store } from '../../store/store';
import { StoreNode, StoreNodeProps } from '../../store/storeNode';

const WS_LOGS = false;

// WebSocket endpoint - must be an absolute URL
// Fallback to origin/graphql-ws if the value is missing
const GRAPHQL_CLIENT_WS_ENDPOINT = import.meta.env.VITE_API_WS_ENDPOINT || `${window.location.origin}/graphql-ws`;
const GRAPHQL_CLIENT_APPSYNC_ENDPOINT = import.meta.env.VITE_API_APPSYNC_ENDPOINT;
const API_KEY = import.meta.env.VITE_API_PUBLIC_KEY;

export class PlayerApiClient
  extends StoreNode {

  readonly store: Store;

  constructor(store: Store, props?: StoreNodeProps) {
    super(store, props);
    makeObservable(this, {
      client: observable,
      authToken: computed,
      apiKey: computed,
      isPublic: computed,
      endpoint: computed,
      host: computed,
      httpsUri: computed,
      wsUri: computed,
      authorization: computed,
      onConnectionError: action,
      onConnectionOpen: action,
      onStart: action,
      onData: action,
      onError: action,
      onComplete: action,
      ensureClient: action,
      subscribeToAddReactions: action,
    });
    this.store = store;
  }

  readonly nodeType = 'UserPlayerApiClient';

  client: WSClient | null = null;

  get authToken(): string | null {
    return this.store.auth.permit?.idToken ?? null;
  }

  get apiKey(): string | null {
    return API_KEY ?? null;
  }

  get isPublic(): boolean {
    return !this.authToken;
  }

  get endpoint(): string | null {
    return GRAPHQL_CLIENT_WS_ENDPOINT ?? null;
  }

  get host(): string | null {
    return GRAPHQL_CLIENT_APPSYNC_ENDPOINT?.replace('https://', '').replace('/graphql', '') ?? null
  }

  get httpsUri(): string | null {
    const { endpoint } = this;
    const origin = window.location.origin;

    if (!endpoint)
      return null;

    if (isValidUrl(endpoint))
      return endpoint;
    else
      return origin + endpoint;
  }

  get wsUri(): string | null {
    const wsUri = this.httpsUri;
    return wsUri?.replace('https', 'wss') ?? null; //.replace('appsync', 'appsync-realtime') || null;
  }

  get authorization() {
    return {
      Authorization: this.authToken ?? 'public',
      host: this.host,
    }
  }

  onConnectionError = () => {
    WS_LOGS && console.log('ws connection error');
  }

  onConnectionOpen = () => {
    WS_LOGS && console.log('ws connection open');
  }

  onStart = () => {
    WS_LOGS && console.log('ws start');
    // TODO: fetch data that was missed during a timeout, determined from the document state
  }

  onData = (data: unknown) => {
    WS_LOGS && console.log('WS Data received', data);
    const subscription = data as AddedCommentSubscription;
    const addedComment = subscription?.addedComment;
    if (!addedComment)
      return;

    const job = this.store.maybeGetJob(addedComment.jobId);
    if (addedComment.reaction) { // TEMP: for filtering out comments
      let reaction;
      if (addedComment.watchMode === WatchMode.Live)
        reaction = new Comment(this.store, addedComment) as Reaction;
      else if (addedComment.watchMode === WatchMode.Static) {
        reaction = job?.insertReaction(addedComment) || null;
        job?.setAddedReaction(reaction);
      }

      this.broadcast('addedReaction', { reaction });
      WS_LOGS && console.log(reaction);
    } //else {
    //   const comment = job?.insertComment(addedComment) || null;
    //   console.log(comment);
    //   this.broadcast('addedComment', { comment });
    // }
  }

  onError = () => {
    WS_LOGS && console.log('ws error');
  }

  onComplete = () => {
    WS_LOGS && console.log('ws complete');
  }

  ensureClient = async () => {
    if (!this.client)
      await this.initClient();
  }

  private async initClient() {
    if (!this.httpsUri || !this.host)
      return;

    this.client = new WSClient({
      endpoint: this.httpsUri,
      host: this.host,
      token: this.authToken,
      key: this.apiKey!,
      onConnectionError: this.onConnectionError,
      onConnectionOpened: this.onConnectionOpen,
      onStart: this.onStart,
      onData: this.onData,
      onError: this.onError,
    });
  }

  async subscribeToAddReactions(args: { jobId: string }) {
    await this.ensureClient();
    await this.client?.open();
    await this.client?.subscribe(
      AddedCommentDocument,
      { jobId: args.jobId }
    );
  }

  async unsubscribe() {
    await this.client?.close();
  }

}
